import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import cn from 'classnames';
import ReactTooltip from 'react-tooltip';
import { removeClickable } from '@Components/Tooltip';
import { KeepFocusWrapper } from '@Hooks/KeepFocus';
import { ButtonWithIcon } from '@els/els-react--button';

import './HeaderInfoTooltip.scss';

import HeaderInfoTooltipEvent from './HeaderInfoTooltipConstants';

const overridePosition = (rightAlign, position, currentEvent, currentTarget, node, place) => {
  // weird bug in current version of library: sometimes parameters get misconfigured and
  // node is String "right" instead of HTMLElement
  if (typeof node.getBoundingClientRect !== 'function') {
    return position;
  }

  if (place === 'bottom') {
    const { width: tootTipWidth } = node.getBoundingClientRect();
    const delta = rightAlign ? tootTipWidth / 2 : -(tootTipWidth / 2);
    const left = Math.ceil(position.left + delta);
    return { ...position, left };
  }

  if (place === 'right' || place === 'left') {
    const { height: tooltipHeight } = node.getBoundingClientRect();
    const top = Math.ceil(position.top + tooltipHeight / 2);
    return { ...position, top };
  }

  return position;
};

function HeaderInfoTooltipContent({ dispatchTooltipHide, children }) {
  return (
    <>
      <ButtonWithIcon
        data-e2e="modal-close"
        type="link"
        size="small"
        linkIconSize="s"
        sprite="Close"
        iconOnly
        onClick={dispatchTooltipHide}
        className="els-btn-modal-close e2e-modal-close">
        Close tooltip
      </ButtonWithIcon>
      {children}
    </>
  );
}

HeaderInfoTooltipContent.propTypes = {
  children: PropTypes.any,
  dispatchTooltipHide: PropTypes.func,
};
/* eslint-disable react-hooks/exhaustive-deps */
function HeaderInfoTooltip({ children, id, rightAlign = false }) {
  const [isShown, setIsShown] = useState(false);
  const tooltipRef = useRef();

  const dispatchTooltipHide = useCallback(() => {
    removeClickable(id);
    window.dispatchEvent(new CustomEvent(HeaderInfoTooltipEvent.HIDE));
  }, []);

  // useCallback to store function reference for document.removeEventListener
  const handleClickOutside = useCallback((event) => {
    try {
      if (!tooltipRef?.current?.tooltipRef.contains(event.target)) {
        dispatchTooltipHide();
      }
    } catch (e) {
      Logger.error('HeaderInfoTooltip Error:', e);
    }
  }, []);

  // useCallback to store function reference for document.removeEventListener
  const handleEsc = useCallback((event) => {
    // eslint-disable-next-line no-unused-expressions
    event.keyCode === 27 && dispatchTooltipHide();
  }, []);

  const afterShowHandler = () => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    setIsShown(true);
  };

  const afterHideHandler = () => {
    document.removeEventListener('mousedown', handleClickOutside);
    document.removeEventListener('keydown', handleEsc);
    setIsShown(false);
  };

  // remove listeners on unmount
  useEffect(
    () => () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    },
    []
  );

  return (
    <ReactTooltip
      ref={tooltipRef}
      id={id}
      delayHide={100}
      className={cn('rs-tooltip-big', { 'rs-tooltip-big-right': rightAlign })}
      effect="solid"
      place="bottom"
      type="custom"
      clickable
      overridePosition={(...args) => overridePosition(rightAlign, ...args)}
      globalEventOff={HeaderInfoTooltipEvent.HIDE}
      afterShow={afterShowHandler}
      afterHide={afterHideHandler}
      disableInternalStyle>
      {isShown ? (
        <KeepFocusWrapper needKeepFocus focusOnMount>
          <HeaderInfoTooltipContent dispatchTooltipHide={dispatchTooltipHide}>{children}</HeaderInfoTooltipContent>
        </KeepFocusWrapper>
      ) : (
        <HeaderInfoTooltipContent dispatchTooltipHide={dispatchTooltipHide}>{children}</HeaderInfoTooltipContent>
      )}
    </ReactTooltip>
  );
}

HeaderInfoTooltip.propTypes = {
  children: PropTypes.any,
  id: PropTypes.string,
  rightAlign: PropTypes.bool,
};

export default HeaderInfoTooltip;
